/**
 * Coaching vocabularies (server: nova/modules/coaching/models.py and service.py). Labels are
 * English source strings marked with msg(); render them with t().
 */
import type { LucideIcon } from 'lucide-react';
import { Car, GraduationCap, Phone, UsersRound, Video } from 'lucide-react';
import type { Tone } from '@/ui';
import { msg, t } from '@/lib/i18n';

export const CASE_STAGES = ['identified', 'planned', 'invited', 'confirmed', 'held', 'follow_up', 'closed', 'cancelled'] as const;
export type CaseStage = (typeof CASE_STAGES)[number];
/** The stages of an open case, in order (the stepper). */
export const OPEN_STAGES: CaseStage[] = ['identified', 'planned', 'invited', 'confirmed', 'held', 'follow_up'];

export const STAGE: Record<string, { label: string; tone: Tone; hint: string }> = {
  identified: { label: msg('Identified'), tone: 'neutral', hint: msg('Selected for coaching; no session planned yet.') },
  planned: { label: msg('Planned'), tone: 'info', hint: msg('A coach, a type and times are set; the invitation is not sent yet.') },
  invited: { label: msg('Invited'), tone: 'accent', hint: msg('The driver has the invitation and the offered times.') },
  confirmed: { label: msg('Confirmed'), tone: 'good', hint: msg('The time is fixed; reminders go out automatically.') },
  held: { label: msg('Held'), tone: 'good', hint: msg('The session took place.') },
  follow_up: { label: msg('Follow-up'), tone: 'caution', hint: msg("The driver's scores are checked after the session.") },
  closed: { label: msg('Closed'), tone: 'neutral', hint: msg('Done.') },
  cancelled: { label: msg('Cancelled'), tone: 'bad', hint: msg('Will not be coached for this need.') },
};

export const SESSION_STATUS: Record<string, { label: string; tone: Tone }> = {
  proposed: { label: msg('Proposed'), tone: 'info' },
  invited: { label: msg('Invited'), tone: 'accent' },
  rescheduled: { label: msg('New times offered'), tone: 'caution' },
  confirmed: { label: msg('Confirmed'), tone: 'good' },
  held: { label: msg('Held'), tone: 'good' },
  no_show: { label: msg('No-show'), tone: 'bad' },
  cancelled: { label: msg('Cancelled'), tone: 'neutral' },
};

export const ACTIVE_SESSION = ['proposed', 'invited', 'confirmed', 'rescheduled'];

export const SESSION_TYPES = ['call', 'video_call', 'in_person', 'ride_along', 'training'] as const;
export const SESSION_TYPE: Record<string, { label: string; icon: LucideIcon }> = {
  call: { label: msg('Phone call'), icon: Phone },
  video_call: { label: msg('Video call'), icon: Video },
  in_person: { label: msg('In person'), icon: UsersRound },
  ride_along: { label: msg('Ride-along'), icon: Car },
  training: { label: msg('Training'), icon: GraduationCap },
};

export const OUTCOMES: Record<string, string> = {
  completed: msg('Completed'),
  partial: msg('Partly done'),
  driver_declined: msg('Driver declined'),
  no_show: msg('No-show'),
};

export const AGREEMENT_STATUS: Record<string, string> = { open: msg('Open'), met: msg('Met'), missed: msg('Missed') };

export const SEVERITY: Record<string, string> = { low: msg('Low'), medium: msg('Medium'), high: msg('High') };

/** Who opened a case (server `CoachingCase.source`). */
export const CASE_SOURCE: Record<string, string> = {
  planner: msg('by the planner'),
  flow: msg('by an automation'),
  manual: msg('by hand'),
  agent: msg('by the assistant'),
  import: msg('imported from the old platform'),
};

export const PHASES = ['preparation', 'scheduling', 'session', 'follow_up'] as const;
export const PHASE: Record<string, string> = {
  preparation: msg('Preparation'),
  scheduling: msg('Scheduling'),
  session: msg('Session'),
  follow_up: msg('Follow-up'),
};

export const BAND: Record<string, { label: string; tone: Tone }> = {
  urgent: { label: msg('Urgent'), tone: 'bad' },
  high: { label: msg('High'), tone: 'caution' },
  medium: { label: msg('Medium'), tone: 'medium' },
  low: { label: msg('Low'), tone: 'good' },
};

export const FACTORS = ['temporal', 'performance', 'trend', 'effectiveness'] as const;
export const FACTOR: Record<string, { label: string; question: string; explain: string }> = {
  temporal: {
    label: msg('Time since coaching'),
    question: msg('Is the driver due?'),
    explain: msg('Days since the last held session against the driver’s interval: overdue drivers score high.'),
  },
  performance: {
    label: msg('Performance in the fleet'),
    question: msg('How does the driver compare?'),
    explain: msg('Position in the fleet’s range per score area over 7 and 30 days: the weakest driver scores 100.'),
  },
  trend: {
    label: msg('Trend'),
    question: msg('Is it getting worse?'),
    explain: msg('Change from 30 days to the last 7 days, and from the longer run to 30 days: worsening scores high.'),
  },
  effectiveness: {
    label: msg('Effect of last session'),
    question: msg('Did the last session help?'),
    explain: msg('Score change in the 30 days after the last session against the 30 days before: no improvement scores high.'),
  },
};

/** Score areas (leaves) a focus may name; labels match the insights vocabulary. */
export const LEAF: Record<string, string> = {
  acceleration: msg('Acceleration'),
  deceleration: msg('Braking'),
  steering: msg('Cornering'),
  tailgating: msg('Following distance'),
  speeding: msg('Speeding'),
  eyes_closed: msg('Fatigue'),
  yawning: msg('Yawning'),
  focus: msg('Attention'),
  phone_use: msg('Phone use'),
  idling: msg('Idling'),
  engine_load: msg('Engine load'),
  cruise_control: msg('Cruise control'),
  coasting: msg('Coasting'),
  safety: msg('Safety'),
  eco: msg('Eco'),
};

export function leafLabel(k: string): string {
  return LEAF[k] ? t(LEAF[k]) : k.replace(/_/g, ' ');
}

export const EVENT_TYPE: Record<string, string> = {
  harsh_accel: msg('Harsh acceleration'),
  harsh_brake: msg('Harsh braking'),
  harsh_corner: msg('Harsh cornering'),
  tailgating: msg('Tailgating'),
  speeding: msg('Speeding'),
  eyes_closed: msg('Eyes closed'),
  yawning: msg('Yawning'),
  distraction: msg('Distraction'),
  phone: msg('Phone use'),
  idling: msg('Long idling'),
  fcw: msg('Forward collision warning'),
  pcw: msg('Pedestrian collision warning'),
  fatigue: msg('Fatigue'),
};

export function eventLabel(k: string): string {
  return EVENT_TYPE[k] ? t(EVENT_TYPE[k]) : k.replace(/_/g, ' ');
}

export const LANGUAGE: Record<string, string> = { en: msg('English'), nl: msg('Dutch'), fr: msg('French'), de: msg('German') };

export const METRIC: Record<string, { label: string; unit: string }> = {
  safety: { label: msg('Safety score'), unit: msg('points') },
  eco: { label: msg('Eco score'), unit: msg('points') },
  fuel_l_100km: { label: msg('Fuel use'), unit: 'L/100 km' },
};

export function label(map: Record<string, { label: string } | string>, key: string | null | undefined): string {
  if (!key) return '—';
  const v = map[key];
  if (!v) return key.replace(/_/g, ' ');
  return t(typeof v === 'string' ? v : v.label);
}
